import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ScrollReveal from "@/components/base/ScrollReveal";
import YouTubeEmbed from "@/components/base/YouTubeEmbed";
import { publicApi, resolveAssetUrl } from "@/lib/api";
import type { BioArtContestPoster, BioArtItem } from "@/lib/types";
import { parseYouTubeVideoId } from "@/lib/youtube";
import { SITE_IMAGES } from "@/lib/siteAssets";

const YEARS = [2021, 2020, 2019, 2018, 2017, 2016, 2015, 2014, 2013] as const;

const POSTER_ZOOM_MIN = 1;
const POSTER_ZOOM_MAX = 4;
const POSTER_ZOOM_STEP = 0.5;

type BioArtSection = {
  name: string;
  items: BioArtItem[];
};

/** 아이템 API가 category.sortOrder 순으로 내려주므로, 최초 등장 순 = 관리자 셀렉트 순 */
function groupBioArtItems(items: BioArtItem[]): BioArtSection[] {
  const groups = new Map<string, BioArtItem[]>();
  const order: string[] = [];

  for (const item of items) {
    const name = item.categoryName ?? "기타";
    if (!groups.has(name)) {
      groups.set(name, []);
      order.push(name);
    }
    groups.get(name)!.push(item);
  }

  return order.map((name) => ({ name, items: groups.get(name)! }));
}

function youtubeThumb(url: string | null): string | null {
  if (!url) return null;
  const id = parseYouTubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

type ContestPosterLightboxProps = {
  year: number;
  poster: BioArtContestPoster;
  onClose: () => void;
};

function ContestPosterLightbox({ year, poster, onClose }: ContestPosterLightboxProps) {
  const [zoom, setZoom] = useState(POSTER_ZOOM_MIN);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);
  const [dragging, setDragging] = useState(false);

  const resetView = useCallback(() => {
    setZoom(POSTER_ZOOM_MIN);
    setOffset({ x: 0, y: 0 });
  }, []);

  const clampZoom = (value: number) =>
    Math.min(POSTER_ZOOM_MAX, Math.max(POSTER_ZOOM_MIN, Math.round(value * 100) / 100));

  const zoomIn = () => {
    setZoom((z) => clampZoom(z + POSTER_ZOOM_STEP));
  };

  const zoomOut = () => {
    setZoom((z) => {
      const next = clampZoom(z - POSTER_ZOOM_STEP);
      if (next <= POSTER_ZOOM_MIN) setOffset({ x: 0, y: 0 });
      return next;
    });
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.deltaY < 0) {
      setZoom((z) => clampZoom(z + POSTER_ZOOM_STEP));
    } else {
      setZoom((z) => {
        const next = clampZoom(z - POSTER_ZOOM_STEP);
        if (next <= POSTER_ZOOM_MIN) setOffset({ x: 0, y: 0 });
        return next;
      });
    }
  };

  const handleDoubleClick = () => {
    if (zoom > POSTER_ZOOM_MIN) {
      resetView();
    } else {
      setZoom(2);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (zoom <= POSTER_ZOOM_MIN) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      originX: offset.x,
      originY: offset.y,
    };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag?.active) return;
    setOffset({
      x: drag.originX + (e.clientX - drag.startX),
      y: drag.originY + (e.clientY - drag.startY),
    });
  };

  const onPointerUp = (e: React.PointerEvent) => {
    if (!dragRef.current?.active) return;
    dragRef.current.active = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    setDragging(false);
  };

  const zoomPercent = Math.round(zoom * 100);
  const canPan = zoom > POSTER_ZOOM_MIN;

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex flex-col bg-black/90"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label={`${year} Bio-Art Contest poster`}
    >
      <div className="absolute top-0 inset-x-0 z-20 flex items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <p className="text-sm sm:text-base font-medium text-white/90 truncate min-w-0">
          {year} Bio-Art Contest
        </p>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={zoomOut}
            disabled={zoom <= POSTER_ZOOM_MIN}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-default"
            aria-label="Zoom out"
          >
            <i className="ri-zoom-out-line text-lg sm:text-xl" />
          </button>
          <button
            type="button"
            onClick={resetView}
            className="min-w-[3.25rem] px-2 h-9 sm:h-10 rounded-full bg-white/10 text-white text-xs font-medium hover:bg-white/20 cursor-pointer transition-colors"
            aria-label="Reset zoom"
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={zoom >= POSTER_ZOOM_MAX}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-40 disabled:cursor-default"
            aria-label="Zoom in"
          >
            <i className="ri-zoom-in-line text-lg sm:text-xl" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/10 text-white hover:bg-white/20 flex items-center justify-center cursor-pointer transition-colors"
            aria-label="Close"
          >
            <i className="ri-close-line text-xl sm:text-2xl" />
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.98 }}
        transition={{ duration: 0.2 }}
        className={`relative z-10 flex-1 flex items-center justify-center min-h-0 w-full overflow-hidden px-2 pt-14 pb-3 sm:px-4 sm:pt-16 sm:pb-4 ${
          canPan ? (dragging ? "cursor-grabbing" : "cursor-grab") : "cursor-zoom-in"
        }`}
        onWheel={handleWheel}
        onDoubleClick={handleDoubleClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={resolveAssetUrl(poster.imageUrl)}
          alt={`${year} Bio-Art Contest poster`}
          draggable={false}
          className={`w-auto h-auto max-w-full select-none touch-none object-contain transition-transform duration-150 ease-out ${
            poster.description
              ? "max-h-[calc(100dvh-8.5rem)]"
              : "max-h-[calc(100dvh-5rem)]"
          }`}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom})`,
            transformOrigin: "center center",
          }}
        />
      </motion.div>

      {poster.description && zoom <= POSTER_ZOOM_MIN && (
        <div className="relative z-20 shrink-0 px-4 pb-5 sm:px-6 sm:pb-6 text-center">
          <p className="text-sm text-white/70 leading-relaxed whitespace-pre-wrap max-w-2xl mx-auto">
            {poster.description}
          </p>
        </div>
      )}

      <p className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none text-[11px] text-white/40 hidden sm:block">
        휠로 확대 · 더블클릭 토글 · 확대 후 드래그
      </p>
    </motion.div>
  );
}

export default function BioArtPage() {
  const [items, setItems] = useState<BioArtItem[]>([]);
  const [contestPoster, setContestPoster] = useState<BioArtContestPoster | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeYear, setActiveYear] = useState<number>(2021);
  const [selected, setSelected] = useState<BioArtItem | null>(null);
  const [posterOpen, setPosterOpen] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [itemRes, posterRes] = await Promise.all([
        publicApi.get<BioArtItem[]>(`/public/bio-art?year=${activeYear}`),
        publicApi.get<BioArtContestPoster>(`/public/bio-art/posters/${activeYear}`).catch(() => ({ data: null })),
      ]);
      if (itemRes.data) setItems(itemRes.data);
      setContestPoster(posterRes.data ?? null);
    } catch {
      setItems([]);
      setContestPoster(null);
    } finally {
      setLoading(false);
    }
  }, [activeYear]);

  useEffect(() => {
    fetchData();
    setPosterOpen(false);
  }, [fetchData]);

  const groupedSections = useMemo(() => groupBioArtItems(items), [items]);
  const displayItems = useMemo(
    () => groupedSections.flatMap((section) => section.items),
    [groupedSections]
  );

  const selectedIndex = useMemo(() => {
    if (!selected) return -1;
    return displayItems.findIndex((it) => it.id === selected.id);
  }, [selected, displayItems]);

  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex >= 0 && selectedIndex < displayItems.length - 1;

  const goToPrev = useCallback(() => {
    if (selectedIndex > 0) {
      setSelected(displayItems[selectedIndex - 1]);
    }
  }, [selectedIndex, displayItems]);

  const goToNext = useCallback(() => {
    if (selectedIndex >= 0 && selectedIndex < displayItems.length - 1) {
      setSelected(displayItems[selectedIndex + 1]);
    }
  }, [selectedIndex, displayItems]);

  useEffect(() => {
    if (!selected && !posterOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelected(null);
        setPosterOpen(false);
      } else if (selected && e.key === "ArrowLeft") goToPrev();
      else if (selected && e.key === "ArrowRight") goToNext();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [selected, posterOpen, goToPrev, goToNext]);

  const cardThumb = (item: BioArtItem) => {
    if (item.mediaType === "youtube") {
      return youtubeThumb(item.youtubeUrl);
    }
    return resolveAssetUrl(item.imageUrl) || null;
  };

  return (
    <div className="min-h-screen bg-white pt-[96px]">
      <section className="relative h-[260px] md:h-[320px] overflow-hidden">
        <img
          src={SITE_IMAGES.pageBanner}
          alt="Bio-Art"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30" />
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-3xl md:text-5xl font-semibold text-white tracking-wide">Bio-Art</h1>
        </div>
      </section>

      <div className="bg-[#f6f9fc] border-b border-gray-100">
        <div className="container mx-auto px-4 py-3">
          <nav className="text-sm text-gray-500">
            <a href="/" className="hover:text-[#1977cc] transition-colors">Home</a>
            <span className="mx-2 text-gray-300">/</span>
            <span className="text-[#2c4964] font-medium">Bio-Art</span>
          </nav>
        </div>
      </div>

      <section className="bg-[#f6f9fc] border-b border-gray-100">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden">
            {/* Year timeline */}
            <div className="px-4 sm:px-6 pt-5 pb-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-gray-400">
                  Year
                </p>
                <p className="text-xs text-gray-400 hidden sm:block">
                  2013 – 2021 Contest Archive
                </p>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r from-white to-transparent z-10 sm:hidden" />
                <div className="pointer-events-none absolute inset-y-0 right-0 w-6 bg-gradient-to-l from-white to-transparent z-10 sm:hidden" />
                <div
                  role="tablist"
                  aria-label="Select year"
                  className="flex gap-1 overflow-x-auto pb-0 -mx-1 px-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  {YEARS.map((year) => {
                    const active = activeYear === year;
                    return (
                      <button
                        key={year}
                        role="tab"
                        aria-selected={active}
                        onClick={() => setActiveYear(year)}
                        className={`relative shrink-0 min-w-[4.5rem] px-3 py-3 text-sm font-semibold transition-colors cursor-pointer ${
                          active
                            ? "text-[#1977cc]"
                            : "text-gray-400 hover:text-[#2c4964]"
                        }`}
                      >
                        {year}
                        {active && (
                          <motion.span
                            layoutId="bio-art-year-indicator"
                            className="absolute left-2 right-2 bottom-0 h-0.5 rounded-full bg-[#1977cc]"
                            transition={{ type: "spring", stiffness: 420, damping: 34 }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
                <div className="h-px bg-gray-100 mt-0" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container mx-auto px-4">
          {!loading && contestPoster && (
            <ScrollReveal>
              <motion.div
                key={`poster-${activeYear}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
                className="mb-10 bg-white rounded-2xl border border-gray-200/80 shadow-sm overflow-hidden"
              >
                <div className="p-5 sm:p-6 md:p-8 flex flex-col sm:flex-row gap-6 sm:gap-8 items-center sm:items-start">
                  <div className="shrink-0 w-full sm:w-auto flex justify-center">
                    <div className="w-[180px] sm:w-[200px] h-[280px] sm:h-[320px] bg-[#f6f9fc] rounded-xl border border-gray-100 flex items-center justify-center overflow-hidden">
                      <img
                        src={resolveAssetUrl(contestPoster.imageUrl)}
                        alt={`${activeYear} Bio-Art Contest poster`}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 text-center sm:text-left flex flex-col justify-center sm:pt-2">
                    <p className="text-xs font-semibold tracking-[0.14em] uppercase text-[#1977cc] mb-2">
                      Contest Poster
                    </p>
                    <h2 className="text-xl md:text-2xl font-semibold text-[#2c4964] mb-3">
                      {activeYear} Bio-Art Contest
                    </h2>
                    {contestPoster.description && (
                      <p className="text-sm text-gray-500 mb-5 leading-relaxed whitespace-pre-wrap">
                        {contestPoster.description}
                      </p>
                    )}
                    <div className={contestPoster.description ? "" : "mt-1"}>
                      <button
                        type="button"
                        onClick={() => setPosterOpen(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#1977cc] text-white text-sm font-medium hover:bg-[#1669b3] transition-colors cursor-pointer"
                        aria-label={`${activeYear} Bio-Art Contest 포스터 전체 보기`}
                      >
                        <i className="ri-zoom-in-line" />
                        자세히 보기
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </ScrollReveal>
          )}

          <ScrollReveal>
            <div className="mb-8">
              <h2 className="text-xl md:text-2xl font-semibold text-[#2c4964]">
                {activeYear}
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeYear}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
              >
                {loading ? (
                  <div className="text-center py-12 text-gray-400">Loading...</div>
                ) : displayItems.length === 0 ? (
                  <div className="text-center py-16">
                    <i className="ri-inbox-line text-3xl text-gray-300 mb-3" />
                    <p className="text-gray-400 text-sm">등록된 작품이 없습니다.</p>
                  </div>
                ) : (
                  <div className="space-y-12">
                    {groupedSections.map((section) => (
                      <div key={section.name}>
                        <h3 className="text-lg font-semibold text-[#2c4964] mb-5 pb-2 border-b border-gray-100">
                          {section.name}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                          {section.items.map((item) => {
                            const thumb = cardThumb(item);
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => setSelected(item)}
                                className="group text-left bg-white rounded-xl border border-gray-100 hover:border-gray-200 transition-all duration-200 overflow-hidden cursor-pointer"
                              >
                                <div className="relative h-52 bg-[#f6f9fc] overflow-hidden flex items-center justify-center">
                                  {thumb ? (
                                    <img
                                      src={thumb}
                                      alt={item.title}
                                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-300">
                                      <i className="ri-image-line text-3xl" />
                                    </div>
                                  )}
                                  {item.mediaType === "youtube" && (
                                    <span className="absolute bottom-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-full bg-black/60 text-white">
                                      <i className="ri-play-fill text-lg" />
                                    </span>
                                  )}
                                </div>
                                <div className="p-5">
                                  <h4 className="text-sm font-semibold text-[#2c4964] leading-snug line-clamp-2">
                                    {item.title}
                                  </h4>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </ScrollReveal>
        </div>
      </section>

      <AnimatePresence>
        {posterOpen && contestPoster && (
          <ContestPosterLightbox
            year={activeYear}
            poster={contestPoster}
            onClose={() => setPosterOpen(false)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {selected && (
          <motion.div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              type="button"
              aria-label="Close overlay"
              className="absolute inset-0 bg-black/60 cursor-pointer"
              onClick={() => setSelected(null)}
            />

            {hasPrev && (
              <button
                type="button"
                onClick={goToPrev}
                aria-label="Previous work"
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line text-2xl" />
              </button>
            )}

            {hasNext && (
              <button
                type="button"
                onClick={goToNext}
                aria-label="Next work"
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line text-2xl" />
              </button>
            )}

            <motion.div
              key={selected.id}
              role="dialog"
              aria-modal="true"
              aria-labelledby="bio-art-modal-title"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl"
            >
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center cursor-pointer"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" />
              </button>

              <div className="bg-[#0f172a]">
                {selected.mediaType === "youtube" && selected.youtubeUrl ? (
                  <div className="p-3 sm:p-4">
                    <YouTubeEmbed url={selected.youtubeUrl} title={selected.title} />
                  </div>
                ) : (
                  <div className="flex items-center justify-center min-h-[240px] max-h-[60vh]">
                    {selected.imageUrl ? (
                      <img
                        src={resolveAssetUrl(selected.imageUrl)}
                        alt={selected.title}
                        className="max-h-[60vh] w-full object-contain"
                      />
                    ) : (
                      <div className="text-gray-400 py-20">
                        <i className="ri-image-line text-4xl" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <p className="text-xs text-[#1977cc] font-medium mb-1">
                      {selected.year} · {selected.categoryName}
                    </p>
                    <h2 id="bio-art-modal-title" className="text-xl font-semibold text-[#2c4964]">
                      {selected.title}
                    </h2>
                  </div>
                  {displayItems.length > 1 && selectedIndex >= 0 && (
                    <p className="shrink-0 text-xs text-gray-400 pt-1">
                      {selectedIndex + 1} / {displayItems.length}
                    </p>
                  )}
                </div>
                {selected.description && (
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                    {selected.description}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
